import { Button } from '@repo/ui/components/button';
import { IconBackspace } from '@tabler/icons-react';
import type { ReactNode } from 'react';

/**
 * The ten digits and a delete, for the one value a cashier types at the start of
 * every shift while standing up.
 *
 * **It exists because the PIN is digits by contract and the till is thumb
 * first.** `PIN_SHAPE` is `^\d{4,6}$` and `docs/ui-conventions.md` sets the till
 * column as *"thumb first, minimal typing"* with `--size-touch` as the floor
 * *"because a mis-tap at the gate costs a queue"* — so a screen that offers a
 * text field and nothing else is asking for the one thing this hardware is worst
 * at. Issue #213.
 *
 * ## It holds no PIN
 *
 * `value` in, `onChange` out, and nothing in between. **A keypad with its own
 * `useState` is a second source of truth for the PIN**, and the case it fails on
 * is not exotic: a cashier types two digits on the keyboard the terminal has
 * plugged into it and taps the other two. Either the typed pair is dropped or
 * the tapped pair overwrites it, and both look like the person mistyped. So the
 * caller hands this the form's own field and the digits go back through the
 * form's own `onChange` — the same function a keystroke goes through, so the two
 * sources cannot diverge because there is only one of them.
 *
 * That is also why there is no `maxLength` here and a `max` instead: the field
 * and the keypad stop at the same digit because they are given the same number,
 * `PIN_MAX_DIGITS`, and not because two files happen to say six.
 *
 * ## What is deliberately not on it
 *
 * **No Enter.** `docs/design-language.md`: *"the submit is `mt-8` away, because
 * the action is not another row of the form."* A keypad that could start the
 * shift would make the green button decorative and put the commit two pixels
 * from the digit above it. Every key is `type="button"` for the same reason —
 * a bare `<button>` inside a `<form>` submits it, which would be an Enter key
 * added by accident rather than on purpose.
 *
 * **No accent.** Mist fills and gauge hairlines, which is what `variant="outline"`
 * already is. Eleven green keys would be eleven primary actions and *Start
 * shift* would be none — the language spends one accent per screen and this
 * screen has already spent it.
 *
 * **No readout.** The digits are on the keys, which are lettering; the value is
 * in the masked field and stays there. Nothing here echoes what has been entered,
 * on a device that stands where the public can see it.
 */
export function PinKeypad({
  value,
  max,
  disabled = false,
  onChange,
  className,
}: {
  /** The PIN as the form currently holds it. */
  value: string;
  /** {@link import('@/lib/field-schemas').PIN_MAX_DIGITS}, from the caller that owns the field. */
  max: number;
  /** While the shift is being started. */
  disabled?: boolean;
  onChange: (value: string) => void;
  className?: string;
}) {
  const full = value.length >= max;

  return (
    // **`role="group"` with a name, and not a `fieldset`.** These are eleven
    // controls that belong together and are announced as such; a fieldset would
    // put a second legend on a screen whose one field label is already above the
    // input these fill.
    //
    // `gap-3` and three equal tracks: at the narrowest this block is allowed to
    // be — `min-w-48`, set by the caller — a column is exactly `--size-touch`,
    // so the floor is kept at the width where it is hardest to keep rather than
    // only at the comfortable one.
    <div
      role="group"
      aria-label="PIN keypad"
      className={`grid grid-cols-3 gap-3 ${className ?? ''}`}
    >
      {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
        <Key
          key={digit}
          disabled={disabled || full}
          onPress={() => {
            onChange(value + digit);
          }}
        >
          {digit}
        </Key>
      ))}

      {/* Bottom-left, where the drawing in issue #213 puts it, and enabled only
          when there is something to take back — a key that cannot do anything
          says so rather than swallowing the tap. */}
      <Key
        label="Delete the last digit"
        disabled={disabled || value.length === 0}
        onPress={() => {
          onChange(value.slice(0, -1));
        }}
      >
        <IconBackspace />
      </Key>

      <Key
        disabled={disabled || full}
        onPress={() => {
          onChange(value + '0');
        }}
      >
        0
      </Key>

      {/* The third cell of the last row is empty, and that is the *no Enter*
          rule taking up space rather than being written down somewhere. */}
    </div>
  );
}

/**
 * One key.
 *
 * **`onMouseDown` is prevented so the field keeps focus.** A tap that moved
 * focus to the key would blur the PIN input, and a blur on a field somebody has
 * already put two digits into reaches the resolver — so the field would go red
 * with *"A PIN is 4 to 6 digits"* while the person is still tapping the third.
 * That is precisely the "telling you off for being unfinished" failure
 * `docs/ui-conventions.md` records, arriving by a route the form's own
 * `onTouched` narrowing cannot see. Preventing the default on `mousedown` stops
 * the focus move for pointer and touch and leaves keyboard focus alone, so a
 * key reached with Tab still focuses and still activates on Enter or Space.
 *
 * **`px-2` overrides the `touch` size's `px-8`.** `Button` merges its variants
 * through `cn`, so a later utility wins: without this a 56px-wide key would have
 * 64px of horizontal padding and the digit would sit outside it. The height and
 * the width floor come from the size variant and stay there — `--size-touch`
 * moves every key at once if the hardware turns out different, which is the
 * whole reason `h-14` is banned.
 */
function Key({
  children,
  label,
  disabled,
  onPress,
}: {
  children: ReactNode;
  label?: string;
  disabled: boolean;
  onPress: () => void;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="touch"
      aria-label={label}
      className="w-full px-2"
      disabled={disabled}
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      onClick={onPress}
    >
      {children}
    </Button>
  );
}
